import React from 'react';

import { TableDataHeader } from '@block-explorer/ui';
import { TableRow, TableRowType } from '@make-software/cspr-design';

import { TabDataTable, DelegationListItem } from '../../../components';
import { Trans, useTranslation } from 'react-i18next';
import {
  useCurrencyRateAmount,
  useGetActiveAccountDelegations,
  useGetPaginatedAccountDelegations,
} from '../../../hooks/event-store-hooks';
import { useSelector } from 'react-redux';
import { RootState } from 'typesafe-actions';

export interface AccountDelegationsListProps {
  publicKey: string;
}

export const AccountDelegationsList = ({
  publicKey,
}: AccountDelegationsListProps) => {
  const { t } = useTranslation();
  const currency = useSelector(
    (state: RootState) => state.currencies.selectedCurrency
  );
  const activePublicKey = useSelector(
    (state: RootState) => state.wallet.activePublicKey
  );

  useGetActiveAccountDelegations(
    activePublicKey
      ? {
          publicKey: activePublicKey,
          includes:
            'bidder,validator_account_info,cspr_name,validator_cspr_name',
          page_size: 250,
        }
      : null
  );

  const {
    data: delegationsData,
    loading,
    error,
    paginationProps,
    sortingProps,
  } = useGetPaginatedAccountDelegations({
    publicKey: publicKey,
    includes: 'account_info,bidder,validator_account_info,validator_cspr_name',
  });

  const { data: rateData, loading: loadingRateData } = useCurrencyRateAmount({
    currencyCode: currency.id.toString(),
  });

  const csprCurrencyRate = rateData?.data?.amount;

  return (
    <TabDataTable
      loading={loading || loadingRateData}
      tableRowType={TableRowType.TextWithAvatar}
      data={delegationsData || []}
      error={error}
      sortingProps={sortingProps}
      paginationProps={paginationProps}
      renderDataHeader={({ sortingProps }) => (
        <TableRow>
          <TableDataHeader fixedWidthRem={35.8}>
            <Trans t={t}>Validator</Trans>
          </TableDataHeader>
          <TableDataHeader fixedWidthRem={35} align="right">
            <Trans t={t}>Commission rate</Trans>
          </TableDataHeader>
          <TableDataHeader
            align="right"
            sortableColumnField={'stake'}
            fixedWidthRem={25}
            {...sortingProps}
          >
            <Trans t={t}>Amount</Trans>
          </TableDataHeader>
          <TableDataHeader align="right">
            <Trans t={t}>Actions</Trans>
          </TableDataHeader>
        </TableRow>
      )}
      renderPaginatedData={(delegations) =>
        delegations.map((data, idx) => (
          <DelegationListItem
            key={data.validator_public_key}
            data={data}
            csprCurrencyRate={csprCurrencyRate}
          />
        ))
      }
    />
  );
};

export default AccountDelegationsList;
